/* Settings - the manual's "Home" settings and its Master Data section.
 *
 * Every figure on the platform rests on an assumption, and this is where the
 * stored assumptions live. Each field says WHAT IT MOVES, not just what it is
 * called.
 *
 * Two kinds, two owners:
 *   general settings   plant-wide - the administrator's alone, because one
 *                      change moves every family at once
 *   master data        per family and per crew - the key user's reference
 *
 * Neither edits the plan. A simulation may assume different master data for
 * itself without touching these.
 *
 * The administrator's accounts screen and the light/dark choice live here
 * too: one place for everything that configures the application.
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Me } from '../api/auth'
import { Users } from './Users'
import type {
  CrewParams,
  Parameters as ParametersData,
  ValueStreamParams,
} from '../api/client'

type Tab = 'general' | 'masterdata' | 'users' | 'appearance'

export function Settings({
  me,
  canWriteSettings,
  canWriteMasterData,
  canManageUsers,
  theme,
  onTheme,
}: {
  me: Me
  canWriteSettings: boolean
  canWriteMasterData: boolean
  canManageUsers: boolean
  theme: 'light' | 'dark'
  onTheme: (theme: 'light' | 'dark') => void
}) {
  const [data, setData] = useState<ParametersData | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>(canWriteSettings ? 'general' : canWriteMasterData ? 'masterdata' : 'appearance')

  const load = () =>
    void api.parameters().then((d) => {
      setData(d)
      setOpen((prev) => prev ?? d.value_streams[0]?.name ?? null)
    })

  useEffect(load, [])

  const announce = (message: string) => {
    setSaved(message)
    setError(null)
    window.setTimeout(() => setSaved(null), 4000)
  }

  const run = async (what: string, action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      load()
      announce(what)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'general', label: 'General settings', show: true },
    { id: 'masterdata', label: 'Master data', show: true },
    { id: 'users', label: 'Users and roles', show: canManageUsers },
    { id: 'appearance', label: 'Appearance', show: true },
  ]

  const current = data?.value_streams.find((v) => v.name === open) ?? data?.value_streams[0]

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card page-head">
        <div className="page-title">
          <h1>Settings</h1>
          <div className="page-sub">
            The stored assumptions behind every figure, the accounts, and how the application
            looks.
          </div>
        </div>
      </div>

      <nav className="tabs" aria-label="Settings sections">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button key={t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
      </nav>

      {error && (
        <div className="banner-error">
          <strong>⚠ Refused.</strong> {error}
        </div>
      )}
      {saved && (
        <div className="banner-ok">
          <strong>✓</strong> {saved}
        </div>
      )}

      {tab === 'appearance' && (
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0 }}>Appearance</h2>
          <p className="small muted">Saved on this browser.</p>
          <div className="choice-grid" style={{ maxWidth: 520 }}>
            {(['light', 'dark'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`choice-card ${theme === t ? 'on' : ''}`}
                onClick={() => onTheme(t)}
              >
                <strong>{t === 'light' ? 'Light mode' : 'Dark mode'}</strong>
                <span className="small muted">
                  {t === 'light' ? 'Light surfaces, for daylight and printing' : 'Dark surfaces, easier on the eyes'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'users' && canManageUsers && <Users me={me} embedded />}

      {(tab === 'general' || tab === 'masterdata') && !data && (
        <div className="card" style={{ padding: 20 }}>Loading…</div>
      )}

      {tab === 'general' && data && (
        <>
          {!canWriteSettings && (
            <div className="banner-info">The general settings can only be changed by an administrator.</div>
          )}
          <ApplicationSettings
            data={data}
            canWrite={canWriteSettings}
            busy={busy}
            onSave={(body) =>
              run('General settings saved. Every report is recalculated from them.', () =>
                api.writeSettings(body),
              )
            }
          />
        </>
      )}

      {tab === 'masterdata' && data && (
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ marginTop: 0 }}>Master data</h2>
        <p className="small" style={{ color: 'var(--text-secondary)' }}>
          The stored reference, per family and per crew. These change{' '}
          <strong>the calculation itself</strong> — the corridor the solver aims at, and what a
          crew produces in a day. To try other values without changing the reference, change
          them in a simulation instead.
          {!canWriteMasterData && ' Read only for your role.'}
        </p>

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14 }}>
          {data.value_streams.map((v) => (
            <button
              key={v.name}
              onClick={() => setOpen(v.name)}
              style={{
                whiteSpace: 'nowrap',
                borderColor: v.name === open ? 'var(--accent)' : 'var(--border)',
                background: v.name === open ? 'var(--surface-3)' : 'var(--surface-1)',
                fontWeight: v.name === open ? 600 : 400,
              }}
            >
              {v.name}
            </button>
          ))}
        </div>

        {current && (
          <>
            <ValueStreamForm
              key={current.id}
              vs={current}
              canWrite={canWriteMasterData}
              busy={busy}
              currency={data.plant.currency}
              onSave={(body) =>
                run(`${current.name}: master data saved.`, () =>
                  api.writeValueStream(current.id, body),
                )
              }
            />
            <h3 style={{ fontSize: 13, margin: '16px 0 8px' }}>
              Crews of {current.name}
            </h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {current.crews.map((c) => (
                <CrewForm
                  key={c.id}
                  crew={c}
                  canWrite={canWriteMasterData}
                  busy={busy}
                  onSave={(body) =>
                    run(`${current.name} · ${c.code}: saved.`, () =>
                      api.writeCrew(c.id, body),
                    )
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
      )}
    </div>
  )
}

/* ------------------------------------------- the manual's Home settings */

function ApplicationSettings({
  data,
  canWrite,
  busy,
  onSave,
}: {
  data: ParametersData
  canWrite: boolean
  busy: boolean
  onSave: (body: ParametersData['plant']['settings']) => void
}) {
  const [form, setForm] = useState(data.plant.settings)
  const dirty = JSON.stringify(form) !== JSON.stringify(data.plant.settings)

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm({ ...form, [key]: value })

  return (
    <div className="card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0 }}>
        General settings{' '}
        <span className="small muted" style={{ fontWeight: 400 }}>
          · plant {data.plant.code}
        </span>
      </h2>
      <p className="small" style={{ color: 'var(--text-secondary)' }}>
        Plant-wide, and changed by the administrator only. These mostly change{' '}
        <strong>the reports</strong>.
      </p>

      <div className="param-grid">
        <Field
          label="Working hours per shift"
          moves="TLO & OT: turns headcount into hours"
        >
          <input
            type="number"
            step="0.01"
            min="0.5"
            max="24"
            value={form.working_hours_per_shift}
            disabled={!canWrite}
            onChange={(e) => set('working_hours_per_shift', Number(e.target.value))}
          />
        </Field>

        <Field label="Direct cost" moves="TLO & OT: multiplies the direct headcount">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.direct_cost_per_hour}
            disabled={!canWrite}
            onChange={(e) => set('direct_cost_per_hour', Number(e.target.value))}
          />
        </Field>

        <Field label="Indirect cost" moves="TLO & OT: multiplies the indirect headcount">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.indirect_cost_per_hour}
            disabled={!canWrite}
            onChange={(e) => set('indirect_cost_per_hour', Number(e.target.value))}
          />
        </Field>

        <Field label="Weeks shown as days" moves="the grid and every report's columns">
          <input
            type="number"
            min="0"
            max="8"
            value={form.daily_bucket_weeks}
            disabled={!canWrite}
            onChange={(e) => set('daily_bucket_weeks', Number(e.target.value))}
          />
        </Field>

        <Field label="Frozen buckets" moves="how much of the horizon the solver may not touch">
          <input
            type="number"
            min="0"
            max="65"
            value={form.frozen_buckets}
            disabled={!canWrite}
            onChange={(e) => set('frozen_buckets', Number(e.target.value))}
          />
        </Field>

        <Field label="Open Orders calculation mode" moves="whether open orders shift by the DOH" wide>
          <select
            value={form.open_orders_mode}
            disabled={!canWrite}
            onChange={(e) => set('open_orders_mode', e.target.value)}
          >
            {data.plant.choices.open_orders_mode.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label} — {o.note}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Calculation logic mode" moves="whether output or efficiency is the input" wide>
          <select
            value={form.calc_mode}
            disabled={!canWrite}
            onChange={(e) => set('calc_mode', e.target.value)}
          >
            {data.plant.choices.calc_mode.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label} — {o.note}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {canWrite && (
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="primary" disabled={!dirty || busy} onClick={() => onSave(form)}>
            Save settings
          </button>
          {dirty && <span className="small muted">unsaved changes</span>}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------ per-family master data */

function ValueStreamForm({
  vs,
  canWrite,
  busy,
  currency,
  onSave,
}: {
  vs: ValueStreamParams
  canWrite: boolean
  busy: boolean
  currency: string
  onSave: (body: {
    min_doh: number
    max_doh: number
    mpw: number
    wsd_minutes: number
    standard_price: number
  }) => void
}) {
  const [form, setForm] = useState({
    min_doh: vs.min_doh,
    max_doh: vs.max_doh,
    mpw: vs.mpw,
    wsd_minutes: vs.wsd_minutes,
    standard_price: vs.standard_price,
  })
  const dirty =
    form.min_doh !== vs.min_doh ||
    form.max_doh !== vs.max_doh ||
    form.mpw !== vs.mpw ||
    form.wsd_minutes !== vs.wsd_minutes ||
    form.standard_price !== vs.standard_price

  const invalid = form.min_doh > form.max_doh

  return (
    <div className="param-grid">
      <Field label="MIN Pipeline Inventory (DOH)" moves="the floor the solver must not go under">
        <input
          type="number"
          step="0.1"
          min="0"
          value={form.min_doh}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, min_doh: Number(e.target.value) })}
        />
      </Field>
      <Field label="MAX Pipeline Inventory (DOH)" moves="the ceiling above which cash is tied up">
        <input
          type="number"
          step="0.1"
          min="0"
          value={form.max_doh}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, max_doh: Number(e.target.value) })}
        />
      </Field>
      <Field label="MPW" moves="the contracted weekly maximum, flagged on Volume">
        <input
          type="number"
          step="1"
          min="0"
          value={form.mpw}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, mpw: Number(e.target.value) })}
        />
      </Field>
      <Field label="WSD (min / piece)" moves="the Activity report, entirely">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.wsd_minutes}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, wsd_minutes: Number(e.target.value) })}
        />
      </Field>
      <Field label={`Standard price (${currency} / piece)`} moves="the Inventory report, entirely">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.standard_price}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, standard_price: Number(e.target.value) })}
        />
      </Field>

      {canWrite && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="primary" disabled={!dirty || busy || invalid} onClick={() => onSave(form)}>
            Save {vs.name}
          </button>
          {invalid && (
            <span className="small" style={{ color: 'var(--status-critical)' }}>
              MIN is above MAX — the corridor would be empty
            </span>
          )}
          {dirty && !invalid && <span className="small muted">unsaved changes</span>}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------- per-crew rows */

function CrewForm({
  crew,
  canWrite,
  busy,
  onSave,
}: {
  crew: CrewParams
  canWrite: boolean
  busy: boolean
  onSave: (body: Omit<CrewParams, 'id' | 'code' | 'line' | 'shift'>) => void
}) {
  const [form, setForm] = useState({
    installed_capacity: crew.installed_capacity,
    crew_pct: crew.crew_pct,
    efficiency: crew.efficiency,
    direct_people: crew.direct_people,
    indirect_people: crew.indirect_people,
    enabled: crew.enabled,
  })
  const dirty = (Object.keys(form) as (keyof typeof form)[]).some(
    (k) => form[k] !== crew[k],
  )

  return (
    <div className="crew-row">
      <div style={{ fontWeight: 600, minWidth: 110 }}>
        Line {crew.line} · Shift {crew.shift}
      </div>
      <Inline label="Install cap." value={form.installed_capacity} step={1}
        disabled={!canWrite} onChange={(v) => setForm({ ...form, installed_capacity: v })} />
      <Inline label="Crew %" value={form.crew_pct} step={0.01}
        disabled={!canWrite} onChange={(v) => setForm({ ...form, crew_pct: v })} />
      <Inline label="Efficiency" value={form.efficiency} step={0.01}
        disabled={!canWrite} onChange={(v) => setForm({ ...form, efficiency: v })} />
      <Inline label="Direct" value={form.direct_people} step={1}
        disabled={!canWrite} onChange={(v) => setForm({ ...form, direct_people: v })} />
      <Inline label="Indirect" value={form.indirect_people} step={1}
        disabled={!canWrite} onChange={(v) => setForm({ ...form, indirect_people: v })} />
      <label className="small" style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={form.enabled}
          disabled={!canWrite}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
        in service
      </label>
      {canWrite && (
        <button className="small" disabled={!dirty || busy} onClick={() => onSave(form)}
          style={{ marginLeft: 'auto' }}>
          {dirty ? 'Save' : 'Saved'}
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- helpers */

function Field({
  label,
  moves,
  wide,
  children,
}: {
  label: string
  moves: string
  wide?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="param-field" style={wide ? { gridColumn: 'span 2' } : undefined}>
      <span className="param-label">{label}</span>
      {children}
      <span className="param-moves">{moves}</span>
    </label>
  )
}

function Inline({
  label,
  value,
  step,
  disabled,
  onChange,
}: {
  label: string
  value: number
  step: number
  disabled: boolean
  onChange: (v: number) => void
}) {
  return (
    <label className="small" style={{ display: 'grid', gap: 2 }}>
      <span className="muted" style={{ fontSize: 10 }}>{label}</span>
      <input
        type="number"
        step={step}
        min={0}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: 82 }}
      />
    </label>
  )
}
